import React, { useState } from 'react';
import styled from 'styled-components';
import { IspPage } from './ispLayout';
import { useQuery } from '@tanstack/react-query';
import {
   AreaChart,
   Area,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   ResponsiveContainer,
} from 'recharts';
import { colors } from '@/constants/colors';
import { Link as RouterLink } from 'react-router-dom';
import { getIspHome, getIspTenant } from '@/api/isp';
import DateRangePicker from '@/components/Admin/UI/DateRangePicker';

/**
 * ISP Business — Dashboard.
 *
 * Ray, 2026-08-28: I had cut this from Phase 1 on the grounds that you cannot
 * chart data you have not collected. He pushed back and was right — a product
 * whose home screen is empty reads as broken, and the numbers here come from
 * data the billing loop records anyway, so it fills from the first payment
 * taken rather than waiting on an analytics feature.
 *
 * What it deliberately does NOT do is invent depth. Four counts and one chart,
 * each answering a question an operator actually asks in the morning: how many
 * customers do I have, who owes me, who is cut off, and what came in. The
 * deeper Finance reporting is a later phase.
 */

const peso = (n: number) =>
   '₱' + Math.round(Number(n ?? 0)).toLocaleString('en-PH', { maximumFractionDigits: 0 });

const IspHome: React.FC = () => {
   // From–to, like the Piso WiFi revenue chart. Empty = the last 30 days.
   const [range, setRange] = useState({ dateFrom: '', dateTo: '' });
   // Who am I, in ISP Business? An operator with no tenant cannot create a
   // plan, a router or a customer — everything is scoped to one. Rather than
   // let them wander through screens that all refuse them, the dashboard IS the
   // setup form until they have set up.
   const { data: tenant, isLoading: tenantLoading } = useQuery({
      queryKey: ['isp-tenant'],
      queryFn: getIspTenant,
      staleTime: 60_000,
   });

   /*
    * 🔴 NOT GATED ON HAVING A TENANT. It used to be `enabled: !!tenant`, so an
    * operator who had not set up ISP Business never fetched at all — `data`
    * stayed undefined and every card showed its `'—'` placeholder for ever.
    * Ray, 2026-09-04: *"is this normal? the value is dash if empty?"*
    *
    * A dash means "we do not know". Zero means "you have none", which is the
    * truth and is the whole point of an empty state. `/api/isp/home` already
    * answers a no-tenant caller with zeroes and an empty 30-day series
    * precisely so this screen has something honest to render — the gate meant
    * that branch was never once called.
    */
   const { data, isLoading, isError } = useQuery({
      queryKey: ['isp-home', range.dateFrom, range.dateTo],
      queryFn: () => getIspHome(range),
      staleTime: 30_000,
   });

   type Tone = 'normal' | 'good' | 'warn' | 'bad';
   const cards: { label: string; value: string; tone: Tone; icon: string }[] = [
      {
         icon: '👥',
         label: 'Active subscribers',
         value: data ? String(data.activeSubscribers) : '—',
         tone: 'normal',
      },
      {
         icon: '💰',
         label: 'Collected this month',
         value: data ? peso(data.collectedThisMonth) : '—',
         tone: 'good',
      },
      {
         /*
          * 🔴 THE NUMBER A BUSINESS IS JUDGED BY. Ray, 2026-09-03: *"this is
          * not useful. this is not a birds eye view of a business operator. it
          * should have total sales."* Four counts and a month's takings say
          * how the month is going and nothing about whether the business is
          * worth running.
          */
         icon: '📈',
         label: 'Total sales, all time',
         value: data ? peso(data.collectedAllTime) : '—',
         tone: 'good',
      },
      {
         // Past their due date but still inside grace — the ones to chase
         // before they become a suspension and a support call.
         icon: '⏰',
         label: 'Overdue',
         value: data ? String(data.overdue) : '—',
         tone: data && data.overdue > 0 ? 'warn' : 'normal',
      },
      {
         icon: '⛔',
         label: 'Suspended',
         value: data ? String(data.suspended) : '—',
         tone: data && data.suspended > 0 ? 'bad' : 'normal',
      },
   ];


   return (
      <IspPage>
         <Title>Dashboard</Title>

         {/* Not a form — Ray, 2026-08-28: general settings belong in Settings,
             the dashboard is for stats. But an operator whose numbers are all
             zero because they have not set up yet needs telling that, and where
             to go, rather than being left to work it out. */}
         {!tenantLoading && !tenant && (
            <Notice>
               Your ISP business is not set up yet, so there is nothing to count.{' '}
               <RouterLink to="/isp-settings">Set it up in Settings</RouterLink>.
            </Notice>
         )}

         <Cards>
            {cards.map((c) => (
               <Card key={c.label} $tone={c.tone}>
                  <CardTop>
                     <CardIcon $tone={c.tone} aria-hidden>
                        {c.icon}
                     </CardIcon>
                     <CardLabel>{c.label}</CardLabel>
                  </CardTop>
                  <CardValue $tone={c.tone}>
                     {isLoading ? '…' : c.value}
                  </CardValue>
               </Card>
            ))}
         </Cards>

         {/* The same chart as Piso WiFi's revenue: title, a coloured total
             per kind, the from–to picker, and the chart drawn even at zero
             (Ray, 2026-10-30). */}
         <Panel>
            <PanelHead>
               <PanelTitle>Collections — cash & online payments</PanelTitle>
               <PanelMeta>
                  <Totals>
                     <Total $c={colors.PRIMARY}>
                        Cash {peso(data?.collectedCash ?? 0)}
                     </Total>
                     <Total $c={colors.SUCCESS}>
                        Online {peso(data?.collectedOnline ?? 0)}
                     </Total>
                  </Totals>
                  <DateRangePicker
                     dateFrom={range.dateFrom}
                     dateTo={range.dateTo}
                     onChange={(from, to) => setRange({ dateFrom: from, dateTo: to })}
                  />
               </PanelMeta>
            </PanelHead>

            {isError && <Empty>Could not load. Try again in a moment.</Empty>}

            {!isError && (
               <ResponsiveContainer width="100%" height={240}>
                  <AreaChart
                     data={data?.collections ?? []}
                     margin={{ top: 5, right: 15, left: 0, bottom: 0 }}
                  >
                     <defs>
                        <linearGradient id="ispCashGrad" x1="0" y1="0" x2="0" y2="1">
                           <stop offset="0%" stopColor={colors.PRIMARY} stopOpacity={0.28} />
                           <stop offset="95%" stopColor={colors.PRIMARY} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="ispOnlineGrad" x1="0" y1="0" x2="0" y2="1">
                           <stop offset="0%" stopColor={colors.SUCCESS} stopOpacity={0.28} />
                           <stop offset="95%" stopColor={colors.SUCCESS} stopOpacity={0} />
                        </linearGradient>
                     </defs>
                     <CartesianGrid strokeDasharray="3 3" stroke={colors.GRAY13} vertical={false} />
                     <XAxis dataKey="label" tick={{ fontSize: 11, fill: colors.GRAY5 }} axisLine={false} tickLine={false} />
                     <YAxis tick={{ fontSize: 11, fill: colors.GRAY5 }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => `₱${v}`} />
                     <Tooltip
                        contentStyle={{ borderRadius: 8, border: `1px solid ${colors.GRAY13}`, fontSize: 12 }}
                        formatter={(value: unknown, name: unknown) => [
                           peso(Number(value)),
                           name === 'online' ? 'Online' : 'Cash',
                        ]}
                     />
                     <Area type="monotone" dataKey="cash" stroke={colors.PRIMARY} strokeWidth={2} fill="url(#ispCashGrad)" />
                     <Area type="monotone" dataKey="online" stroke={colors.SUCCESS} strokeWidth={2} fill="url(#ispOnlineGrad)" />
                  </AreaChart>
               </ResponsiveContainer>
            )}
         </Panel>
      </IspPage>
   );
};

export default IspHome;


const Title = styled.h1`
   margin: 0;
   font-size: 20px;
`;

/** The colour each tone draws with — value, icon chip and the bar on top. */
const toneColor = (tone: 'normal' | 'good' | 'warn' | 'bad') =>
   tone === 'good'
      ? colors.SUCCESS
      : tone === 'warn'
        ? colors.ORANGE
        : tone === 'bad'
          ? colors.RED
          : colors.PRIMARY;

/**
 * Five figures, five columns, one row (Ray, 2026-10-30: "fit the 5 card in 1
 * row to not look ugly" — 4 + 1 left the fifth alone on a row). On a phone:
 * 2 columns with the first card spanning both, so nothing is orphaned.
 */
const Cards = styled.div`
   display: grid;
   grid-template-columns: repeat(5, minmax(0, 1fr));
   gap: 12px;

   /* Mid widths: 3 on top, 2 wider below — still no gap. */
   @media (max-width: 1100px) {
      grid-template-columns: repeat(6, minmax(0, 1fr));
      & > :nth-child(-n + 3) {
         grid-column: span 2;
      }
      & > :nth-child(n + 4) {
         grid-column: span 3;
      }
   }
   @media (max-width: 720px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      & > :nth-child(n) {
         grid-column: span 1;
      }
      & > :first-child {
         grid-column: span 2;
      }
   }
`;

const Card = styled.div<{ $tone: 'normal' | 'good' | 'warn' | 'bad' }>`
   position: relative;
   overflow: hidden;
   border: 1px solid var(--app-border);
   border-radius: 8px;
   padding: 14px 14px 12px;
   display: flex;
   flex-direction: column;
   gap: 10px;
   background: linear-gradient(
      160deg,
      ${({ $tone }) => toneColor($tone)}12 0%,
      transparent 60%
   );

   &::before {
      content: '';
      position: absolute;
      inset: 0 0 auto 0;
      height: 3px;
      background: ${({ $tone }) => toneColor($tone)};
   }
`;

const CardTop = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   min-width: 0;
`;

const CardIcon = styled.span<{ $tone: 'normal' | 'good' | 'warn' | 'bad' }>`
   flex: 0 0 30px;
   height: 30px;
   border-radius: 8px;
   display: grid;
   place-items: center;
   font-size: 16px;
   background: ${({ $tone }) => toneColor($tone)}1f;
`;

const CardLabel = styled.span`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY4};
   line-height: 1.25;
`;

const CardValue = styled.strong<{ $tone: 'normal' | 'good' | 'warn' | 'bad' }>`
   font-size: 26px;
   font-weight: 800;
   line-height: 1.1;
   font-variant-numeric: tabular-nums;
   color: ${({ $tone }) =>
      $tone === 'good'
         ? colors.SUCCESS
         : $tone === 'warn'
           ? colors.ORANGE
           : $tone === 'bad'
             ? colors.RED
             : 'inherit'};
`;

const Panel = styled.div`
   border: 1px solid var(--app-border);
   border-radius: 10px;
   padding: 14px;
`;

const PanelHead = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   margin-bottom: 10px;
`;

const PanelTitle = styled.h2`
   margin: 0;
   font-size: 14px;
`;

const Empty = styled.p`
   margin: 0;
   padding: 40px 0;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY4};
`;

const Notice = styled.div`
   border: 1px solid var(--app-border);
   border-radius: 10px;
   padding: 12px 14px;
   font-size: 13px;
   line-height: 1.6;
   color: ${colors.GRAY4};

   a {
      color: ${colors.PRIMARY};
   }
`;

const PanelMeta = styled.div`
   display: flex;
   align-items: center;
   flex-wrap: wrap;
   gap: 16px;
`;

const Totals = styled.div`
   display: flex;
   align-items: center;
   gap: 14px;
`;

const Total = styled.span<{ $c: string }>`
   font-size: 13px;
   font-weight: 700;
   color: ${({ $c }) => $c};
   display: inline-flex;
   align-items: center;
   gap: 6px;

   &::before {
      content: '';
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background: ${({ $c }) => $c};
   }
`;
